/**
 * Canonical JSON serialization for substrate audit records.
 *
 * The substrate's tamper-evidence depends on multi-party signatures verifying
 * over byte-identical canonical content. Each party (SHN-PBC, SHDT, holders,
 * SHGT-witness) signs the same canonical bytes; if even one byte differs
 * between parties' inputs, signatures will diverge and consensus-divergence
 * detection fires.
 *
 * Per ADR-010 §1.2 (post-W6 rewrite), substrate canonicalizes audit records
 * using **RFC 8785 JSON Canonicalization Scheme (JCS)**. This is a Phase 1
 * binding commitment — the W6 v0 sort-keys implementation was acceptable when
 * a single operator held all signing keys and produced all canonical bytes,
 * but the customer-deployable doctrine forces canonicalization to be
 * deterministic across environments running different JSON serializers,
 * different Node versions, and different language runtimes. RFC 8785 is the
 * IETF standard for this; its conformance vectors are published at
 * https://datatracker.ietf.org/doc/html/rfc8785#appendix-B.
 *
 * Implementation: we delegate canonicalization to the `canonicalize` npm
 * package (Apache-2.0, maintained by Samuel Erdtman, RFC 8785 co-author). The
 * package is the de facto reference implementation and passes the RFC's own
 * conformance test suite. See `test/canonicalize.test.ts` for our own
 * conformance vectors.
 *
 * What this module adds on top of the base library:
 *
 *   1. Input validation. RFC 8785 itself doesn't reject NaN/Infinity/BigInt —
 *      the library throws for some but not others. Substrate audit records
 *      should never contain non-finite numbers, BigInts, functions, or
 *      symbols; we reject these explicitly with structured errors so
 *      misuse fails loudly at sign time rather than silently producing
 *      records that won't round-trip.
 *
 *   2. The `canonicalizeForSigning` wrapper that strips fields excluded from
 *      signed content (signatures, ledgerTransactionId — see the EXCLUDED
 *      set below). Per Tech Spec §4.8, signatures are computed over the
 *      record content excluding the signatures field itself; ledger
 *      transaction IDs are populated post-signing.
 *
 * Library version pinning: `canonicalize@^3.0.0`. RFC 8785 is a stable
 * standard, so library version drift is not expected to change canonical
 * output. The integration tests verify this — if a future version produces
 * different bytes, the conformance-vector tests will fail.
 *
 * **Why this lives in `@shn/audit-canonical` (not in audit-signing-service):**
 * external verifiers — auditors, regulators, partners running
 * `@shn/audit-verifier` — must recompute canonical bytes independently of
 * the signer service. Otherwise the verifier would be verifying signer-
 * supplied bytes, which collapses the trust property. Both audit-signing-
 * service (signer side) and audit-verifier (external-verifier side) consume
 * this package; there is exactly one canonicalization implementation in the
 * substrate so signer and verifier cannot drift.
 */
import canonicalizeJcs from 'canonicalize';
import { SubstrateError } from '@shn/substrate-types';
/**
 * Fields excluded from canonicalization because they are derived after signing
 * or are themselves the signatures.
 */
const EXCLUDED_FIELDS = new Set(['signatures', 'ledgerTransactionId']);
/**
 * Canonicalize a record-like object for signing. Returns UTF-8 bytes per
 * RFC 8785 (which mandates UTF-8 output).
 *
 * @throws SubstrateError with code 'invalid-input' if the record contains
 * functions, symbols, BigInts, or non-finite numbers that JSON cannot
 * represent.
 */
export function canonicalizeForSigning(record) {
    const filtered = filterExcluded(record);
    validateNoUnsupported(filtered);
    const canonical = canonicalizeJcs(filtered);
    if (canonical === undefined) {
        // The library returns undefined only when the input itself is undefined
        // (e.g., an undefined top-level value). We've already rejected undefined
        // via validateNoUnsupported, so this should be unreachable.
        throw new SubstrateError('Canonicalization produced no output (input was undefined)', 'invalid-input');
    }
    return new TextEncoder().encode(canonical);
}
/**
 * Canonical JSON serialization (RFC 8785 JCS). Returns the canonical string
 * form; use `canonicalizeForSigning` if you want UTF-8 bytes for signing.
 *
 * Exposed for test purposes and for callers who need the string form. Note
 * that this does NOT strip excluded fields — callers who need that should
 * use `canonicalizeForSigning`.
 *
 * @throws SubstrateError with code 'invalid-input' for unsupported types.
 */
export function canonicalize(value) {
    validateNoUnsupported(value);
    const result = canonicalizeJcs(value);
    if (result === undefined) {
        throw new SubstrateError('Cannot canonicalize undefined value', 'invalid-input');
    }
    return result;
}
/**
 * Strip excluded fields from the top level of the record.
 */
function filterExcluded(record) {
    if (record === null || typeof record !== 'object') {
        return record;
    }
    const result = {};
    for (const [key, value] of Object.entries(record)) {
        if (EXCLUDED_FIELDS.has(key))
            continue;
        result[key] = value;
    }
    return result;
}
/**
 * Walk the value graph and reject anything that audit records should never
 * contain. The library is permissive (BigInt produces 'undefined', for
 * example, which is silently dropped — that would be a security bug for
 * audit content). Better to fail loudly than to silently drop fields.
 *
 * Allowed types: null, boolean, finite number, string, array, plain object.
 * Disallowed: undefined (top-level), bigint, function, symbol, NaN, Infinity.
 *
 * **PL3 nested-EXCLUDED-key smuggling closure** (M3 finding per CTO 2026-05-12
 * 21:31Z Constitutional Tier A severity announce + VPE substantive composition
 * cycle routing). `EXCLUDED_FIELDS` ('signatures' + 'ledgerTransactionId') are
 * reserved substrate fields that `filterExcluded` strips at the top level of
 * `canonicalizeForSigning` so signatures are computed over content excluding
 * the signatures themselves. The M3 smuggling vector: an attacker injects a
 * `signatures` or `ledgerTransactionId` key NESTED inside record content
 * (e.g., `record.metadata.signatures = [forgedFromOldRecord]`) hoping the
 * top-level-only filter lets it survive canonicalization into the signed
 * bytes — a credential-laundering primitive against the audit chain.
 *
 * `validateNoUnsupported` rejects nested occurrences with a structured error
 * (Option B per VPE PL3 routing description: "forbid `signatures`/
 * `ledgerTransactionId` nested keys in `validateNoUnsupported`"). Top-level
 * occurrences pass for `canonicalize()` general-purpose callers (the
 * `canonicalizeForSigning` flow has already stripped them via `filterExcluded`
 * before `validateNoUnsupported` runs).
 */
function validateNoUnsupported(value, path = '') {
    if (value === null)
        return;
    switch (typeof value) {
        case 'boolean':
        case 'string':
            return;
        case 'number':
            if (!Number.isFinite(value)) {
                throw new SubstrateError(`Cannot canonicalize non-finite number (NaN, Infinity) at ${path || '<root>'}`, 'invalid-input');
            }
            return;
        case 'bigint':
            throw new SubstrateError(`Cannot canonicalize bigint (not representable in JSON) at ${path || '<root>'}`, 'invalid-input');
        case 'function':
        case 'symbol':
            throw new SubstrateError(`Cannot canonicalize ${typeof value} value at ${path || '<root>'}`, 'invalid-input');
        case 'undefined':
            throw new SubstrateError(`Cannot canonicalize undefined value at ${path || '<root>'}`, 'invalid-input');
        case 'object': {
            if (Array.isArray(value)) {
                for (let i = 0; i < value.length; i++) {
                    // RFC 8785 explicitly maps undefined inside arrays to null (matching
                    // JSON.stringify), so we accept undefined array elements without
                    // erroring. The library handles this conversion.
                    if (value[i] === undefined)
                        continue;
                    validateNoUnsupported(value[i], `${path}[${i}]`);
                }
                return;
            }
            // Plain object. Inside objects, undefined values are dropped by
            // JSON.stringify and by canonicalize(); we tolerate them at object
            // level, only rejecting top-level undefined.
            for (const [key, v] of Object.entries(value)) {
                if (v === undefined)
                    continue;
                // PL3 closure: forbid EXCLUDED_FIELDS at any nested position. Top-
                // level is detected via `path === ''` (we are iterating children of
                // the root). For canonicalizeForSigning, the root has already been
                // run through filterExcluded so top-level EXCLUDED keys are gone;
                // for canonicalize() general-purpose callers, top-level usage is
                // preserved unchanged (backward-compat).
                if (path !== '' && EXCLUDED_FIELDS.has(key)) {
                    throw new SubstrateError(`Cannot canonicalize: nested field '${key}' is reserved for substrate use (audit-chain integrity; PL3 nested-smuggling closure) at ${`${path}.${key}`}`, 'invalid-input');
                }
                validateNoUnsupported(v, path ? `${path}.${key}` : key);
            }
            return;
        }
    }
}
//# sourceMappingURL=canonicalize.js.map